import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import ModalFormWrapper, { ModalFormLayout } from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';

import {
  DEFAULT_FORM,
  DESCRIPTION_MAX_LENGTH,
  TITLE_MAX_LENGTH,
} from './AppraisalRevisionForm.constants';
import { AppraisalRevisionFormTestIds } from './AppraisalRevisionForm.testIds';
import { schema } from './AppraisalRevisionForm.utils';

const AppraisalRevisionForm = ({ isLoading, setIsLoading, onSubmit }) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, validationErrors, setSchema, handleChange, validateForm } =
    useForm(DEFAULT_FORM);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onSubmitForm = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    try {
      setIsLoading(true);

      await onSubmit({ title: form.title.trim(), description: form.description.trim() });
      dispatch(closeDialogAction());
    } finally {
      setIsLoading(false);
    }
  }, [validateForm, form, onSubmit, setIsLoading, dispatch]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  return (
    <ModalFormWrapper
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      layout={ModalFormLayout.EMBEDDED}
      submitButtonName="Request Revision"
      onClose={onClose}
      onSubmit={onSubmitForm}
    >
      <TextFieldCDS
        autoFocus
        fullWidth
        required
        dataTestId={AppraisalRevisionFormTestIds.TITLE_INPUT}
        disabled={isLoading}
        error={!!validationErrors.title}
        helperText={validationErrors.title}
        label="Title"
        maxLength={TITLE_MAX_LENGTH}
        name="title"
        value={form.title}
        variant="outlined"
        onChange={handleChange}
      />
      <TextFieldCDS
        fullWidth
        multiline
        required
        dataTestId={AppraisalRevisionFormTestIds.DESCRIPTION_INPUT}
        disabled={isLoading}
        error={!!validationErrors.description}
        helperText={validationErrors.description}
        label="Description"
        maxLength={DESCRIPTION_MAX_LENGTH}
        name="description"
        rows={4}
        value={form.description}
        variant="outlined"
        onChange={handleChange}
      />
    </ModalFormWrapper>
  );
};

AppraisalRevisionForm.propTypes = {
  ...WithLoadingPropTypes,
  onSubmit: PropTypes.func.isRequired,
};

export default compose(withLoading, memo)(AppraisalRevisionForm);
